import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { actorOf, can, type Actor, type Capability } from '@saledeed/shared';
import { onSessionChange, refresh, type SessionUser } from '../api/client';

type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: SessionUser; actor: Actor };

const SessionContext = createContext<SessionState>({ status: 'loading' });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    onSessionChange(user => setState(user
      ? { status: 'authenticated', user, actor: actorOf(user.role, user.execSubtype) }
      : { status: 'anonymous' }));
    // A page load has no access token; the refresh cookie (if any) restores the session.
    void refresh().then(user => { if (!user) setState({ status: 'anonymous' }); });
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}

/** The signed-in user. Only call below the authenticated layout. */
export function useUser() {
  const s = useContext(SessionContext);
  if (s.status !== 'authenticated') throw new Error('useUser outside an authenticated area');
  return s;
}

/**
 * For showing or hiding controls only. The Worker checks every request on its
 * own; hiding a button here is never the access control.
 */
export function useCan(cap: Capability): boolean {
  const s = useContext(SessionContext);
  return s.status === 'authenticated' && can(s.actor, cap);
}
